/**
 * PUBLIC PAGE per congregation (F3) — the public link of a church.
 *
 * REAL guest-first: mounts at router root (outside `_app`), requires no
 * account, mounts neither PowerSync nor session — consumes ONLY public endpoints
 * (`GET /api/public/churches/:id` + `/meetings`), que exponen exclusivamente
 * public data (same predicate as stream `public_meetings`; test in
 * apps/server/tests/administration.rs). Upcoming activities are
 * materialized with SAME recurrence engine used by app.
 *
 * Socials: clickable LINKS are displayed (WhatsApp/Facebook/YouTube...);
 * provider embed/stream is manual (products/agape.md §7).
 */
import { createRoute } from "@tanstack/solid-router";
import { createMemo, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { materializeOccurrences, parseSocialLinks, type SocialKind } from "@agape/shared";
import {
  CalendarDays,
  Camera,
  Church as ChurchIcon,
  CirclePlay,
  Globe,
  Link as LinkIcon,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Share2,
} from "@fleet/icons";

import { rootRoute } from "@/routes/__root";
import { SERVER_BASE_URL } from "@/lib/api";
import { AppBackground } from "@/components/app-background";
import { GlassSurface } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

interface PublicChurch {
  id: string;
  name: string;
  description?: string | null;
  history?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  website?: string | null;
  socials?: string | null;
  timezone?: string | null;
}

interface PublicMeeting {
  id: string;
  title: string;
  description?: string | null;
  kind?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  rrule?: string | null;
  locationDetail?: string | null;
  venueName?: string | null;
}

interface PublicMeetingsResponse {
  meetings: PublicMeeting[];
  exceptions: {
    meetingId: string;
    occurrenceStart?: string | null;
    kind?: string | null;
    newStart?: string | null;
    newEnd?: string | null;
  }[];
}

const API = SERVER_BASE_URL.replace(/\/$/, "");

async function fetchJson<T>(path: string): Promise<T | null> {
  const res = await fetch(`${API}${path}`);
  if (!res.ok) {
    return null;
  }
  // Trust boundary of own public endpoint (shape fixed by utoipa).
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await res.json()) as T;
}

// Lucide 1.x no longer provides brand icons: they are represented with generic
// equivalents (the LINK is what matters; provider embed is manual).
function socialIcon(kind: SocialKind) {
  switch (kind) {
    case "whatsapp": {
      return <MessageCircle size={16} />;
    }
    case "facebook": {
      return <Share2 size={16} />;
    }
    case "youtube": {
      return <CirclePlay size={16} />;
    }
    case "instagram": {
      return <Camera size={16} />;
    }
    default: {
      return <LinkIcon size={16} />;
    }
  }
}

function formatOccurrence(startMs: number, timezone?: string | null): string {
  return new Date(startMs).toLocaleString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone ?? undefined,
  });
}

export function PublicChurchPage() {
  const { t } = useI18n();
  const params = publicChurchRoute.useParams();

  const [church] = createResource(
    () => params().churchId,
    (id) => fetchJson<PublicChurch>(`/api/public/churches/${encodeURIComponent(id)}`),
  );
  const [agenda] = createResource(
    () => params().churchId,
    (id) =>
      fetchJson<PublicMeetingsResponse>(`/api/public/churches/${encodeURIComponent(id)}/meetings`),
  );

  const socials = createMemo(() => parseSocialLinks(church()?.socials));

  /** Upcoming public occurrences (90 days), cancelled excluded. */
  const upcoming = createMemo(() => {
    const data = agenda();
    if (!data) {
      return [];
    }
    const now = Date.now();
    const window = { from: now - 60 * 60 * 1000, to: now + 90 * 24 * 60 * 60 * 1000 };
    const exceptions = data.exceptions.map((e, i) => ({
      id: `ex-${i}`,
      meeting_id: e.meetingId,
      occurrence_start: e.occurrenceStart ?? null,
      kind: e.kind ?? null,
      new_start: e.newStart ?? null,
      new_end: e.newEnd ?? null,
    }));
    return data.meetings
      .flatMap((m) =>
        materializeOccurrences(
          {
            id: m.id,
            starts_at: m.startsAt ?? null,
            ends_at: m.endsAt ?? null,
            timezone: m.timezone ?? null,
            rrule: m.rrule ?? null,
          },
          window,
          exceptions,
        ).map((o) => ({ occurrence: o, meeting: m })),
      )
      .filter(
        ({ occurrence }) =>
          occurrence.status !== "cancelled" && occurrence.start >= now - 60 * 60 * 1000,
      )
      .toSorted((a, b) => a.occurrence.start - b.occurrence.start)
      .slice(0, 10);
  });

  return (
    <div class="relative min-h-dvh overflow-y-auto">
      <AppBackground />
      <main class="mx-auto w-full max-w-2xl space-y-4 px-4 py-8" data-testid="public-church-page">
        <Show
          when={church()}
          fallback={
            <Show when={!church.loading}>
              <GlassSurface class="p-6 text-center">
                <p class="text-lg font-semibold" data-testid="public-church-not-found">
                  {t(msg`This congregation page is not available.`)}
                </p>
              </GlassSurface>
            </Show>
          }
        >
          {(c) => (
            <>
              <GlassSurface class="space-y-3 p-6">
                <div class="flex items-center gap-3">
                  <span class="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                    <ChurchIcon size={24} />
                  </span>
                  <div class="min-w-0">
                    <h1 class="truncate text-2xl font-bold" data-testid="public-church-name">
                      {c().name}
                    </h1>
                    <Show when={c().city || c().country}>
                      <p class="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin size={14} />
                        {[c().city, c().country].filter(Boolean).join(", ")}
                      </p>
                    </Show>
                  </div>
                </div>
                <Show when={c().description}>
                  <p class="text-muted-foreground" data-testid="public-church-description">
                    {c().description}
                  </p>
                </Show>
                <div class="flex flex-wrap gap-3 text-sm">
                  <Show when={c().address}>
                    <span class="flex items-center gap-1 text-muted-foreground">
                      <MapPin size={14} /> {c().address}
                    </span>
                  </Show>
                  <Show when={c().phone}>
                    <a href={`tel:${c().phone}`} class="flex items-center gap-1 hover:underline">
                      <Phone size={14} /> {c().phone}
                    </a>
                  </Show>
                  <Show when={c().email}>
                    <a href={`mailto:${c().email}`} class="flex items-center gap-1 hover:underline">
                      <Mail size={14} /> {c().email}
                    </a>
                  </Show>
                  <Show when={c().website}>
                    <a
                      href={c().website ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-1 hover:underline"
                    >
                      <Globe size={14} /> {t(msg`Website`)}
                    </a>
                  </Show>
                </div>
                <Show when={socials().length > 0}>
                  <div class="flex flex-wrap gap-2" data-testid="public-church-socials">
                    <For each={socials()}>
                      {(link) => (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/80"
                          data-testid={`public-social-${link.kind}`}
                        >
                          {socialIcon(link.kind)}
                          <span class="capitalize">{link.kind}</span>
                        </a>
                      )}
                    </For>
                  </div>
                </Show>
              </GlassSurface>

              <GlassSurface class="space-y-3 p-6">
                <h2 class="flex items-center gap-2 text-lg font-semibold">
                  <CalendarDays size={18} /> {t(msg`Upcoming activities`)}
                </h2>
                <Show
                  when={upcoming().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground" data-testid="public-agenda-empty">
                      {t(msg`No public activities scheduled right now.`)}
                    </p>
                  }
                >
                  <ul class="space-y-2" data-testid="public-agenda">
                    <For each={upcoming()}>
                      {({ occurrence, meeting }) => (
                        <li
                          class="rounded-xl border border-border/60 bg-background/40 px-4 py-3"
                          data-testid="public-agenda-item"
                        >
                          <p class="font-medium">{meeting.title}</p>
                          <p class="text-sm capitalize text-muted-foreground">
                            {formatOccurrence(occurrence.start, meeting.timezone)}
                            <Show when={meeting.venueName}>
                              {" · "}
                              {meeting.venueName}
                              <Show when={meeting.locationDetail}> — {meeting.locationDetail}</Show>
                            </Show>
                          </p>
                          <Show when={meeting.description}>
                            <p class="mt-1 text-sm text-muted-foreground">{meeting.description}</p>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </GlassSurface>

              <Show when={c().history}>
                <GlassSurface class="space-y-2 p-6">
                  <h2 class="text-lg font-semibold">{t(msg`Our story`)}</h2>
                  <p class="whitespace-pre-wrap text-sm text-muted-foreground">{c().history}</p>
                </GlassSurface>
              </Show>
            </>
          )}
        </Show>
      </main>
    </div>
  );
}

export const publicChurchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$churchId",
  component: PublicChurchPage,
});
